'use client';
import cn from 'classnames';
import Image from 'next/image';
import NextLink from 'next/link';

import Link from '@/components/common/Link/Link';
import Text from '@/components/common/Text/Text';
import WishlistIcon from '@/components/common/WishlistIcon/WishlistIcon';
import { useContextBroker } from '@/components/context/ContextBroker/useContextBroker';

import { ProductCardData } from '@/types/Product';
import { USDFormatter } from '@/util/currency';
import { generateEllaProductUrl } from '@/util/productUrl';

import FallbackImage from 'public/img/fallback-image.jpg';

import './ProductCard.style.scss';
import { getAncestorSku } from '@/util/sku';
import {
  displayTierName,
  fetchProductCardDataById
} from '@/util/product_client_safe';
import { useEffect, useState } from 'react';

export enum PRODUCT_CARD_TYPE {
  REVOLVE = 'revolve',
  VIVRELLE_WITH_BRAND = 'vivrelle-with-brand'
}

export type ProductCardProps = {
  className?: string;
  data: ProductCardData;
  newTab?: boolean;
  handleProductSelect?: (action: 'ADD' | 'REMOVE') => Promise<void>;
  wishlistAnalytics?: (item: ProductCardData) => void;
  /** Set to true if we want small, fixed-sized version of card */
  small?: boolean;
  variant?: 'ella-styling' | string;
  price?: number;
  productType?: PRODUCT_CARD_TYPE;
  productSelected?: boolean;
  /** Optional click interceptor. Call e.preventDefault() to prevent navigation. */
  onProductCardClick?: (e: React.MouseEvent) => void;
};

export const PRODUCT_CARD_IMG_HEIGHT = 900;
export const PRODUCT_CARD_IMG_WIDTH = 750;

export function ProductCardContent(props: ProductCardProps) {
  const {
    data,
    variant,
    price,
    productType,
    productSelected,
    handleProductSelect
  } = props;

  const [displayImage, setDisplayImage] = useState<string>(
    data.mainImage || data.hoverImage || FallbackImage.src
  );

  const { isIOSWebView } = useContextBroker();
  const productUrl = generateEllaProductUrl(data, isIOSWebView);

  useEffect(() => {
    async function loadAncestorImage() {
      if (!data.mainImage && !data.hoverImage) {
        try {
          const ancestorSku = getAncestorSku(data.sku);
          if (ancestorSku === 'P' || ancestorSku === 'PHFE') {
            return;
          }
          const response = await fetch(`/api/product/sku/${ancestorSku}`);
          const { productId } = await response.json();
          if (!productId) {
            return;
          }
          const ancestorData = await fetchProductCardDataById(productId);
          if (ancestorData.mainImage) {
            setDisplayImage(ancestorData.mainImage);
          }
        } catch (error) {
          setDisplayImage(FallbackImage.src);
        }
      }
    }
    loadAncestorImage();
  }, [data.sku, data.mainImage, data.hoverImage]);

  return (
    <>
      <>
        {handleProductSelect ? (
          <div
            className={cn('ProductCard-visual-container')}
            onClick={() =>
              handleProductSelect(productSelected ? 'REMOVE' : 'ADD')
            }
          >
            {data.borrowed && (
              <Text
                className='item-borrowed-text'
                variant='text-s'
              >
                Borrowed
              </Text>
            )}
            <Image
              className='main-img'
              src={displayImage}
              height={PRODUCT_CARD_IMG_HEIGHT}
              width={PRODUCT_CARD_IMG_WIDTH}
              alt={data.name}
            />
          </div>
        ) : (
          <div
            className={cn('ProductCard-visual-container', {
              'has-hover-img': !!data.hoverImage
            })}
          >
            {data.borrowed && (
              <Text
                className='item-borrowed-text'
                variant='text-s'
              >
                Borrowed
              </Text>
            )}
            <Image
              className='main-img'
              src={displayImage}
              height={PRODUCT_CARD_IMG_HEIGHT}
              width={PRODUCT_CARD_IMG_WIDTH}
              alt={data.name}
            />
            {data.hoverImage && (
              <Image
                className='hover-img'
                src={data.hoverImage}
                height={PRODUCT_CARD_IMG_HEIGHT}
                width={PRODUCT_CARD_IMG_WIDTH}
                alt={`Alternate view angle of ${data.name}`}
              />
            )}
          </div>
        )}
      </>

      <div className='ProductCard-title-container'>
        <div className='title-group'>
          {variant === 'ella-styling' ? (
            <>
              {productType === 'vivrelle-with-brand' ? (
                <>
                  <Text
                    className='title-brand'
                    variant='title-xxs-500'
                  >
                    {data.brand}
                  </Text>
                  <Text
                    className='title-name'
                    variant='text-s'
                  >
                    <NextLink
                      href={productUrl}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {data.name}
                    </NextLink>
                  </Text>
                </>
              ) : (
                <>
                  <Text
                    className='ProductCard-title-ella'
                    variant='text-s-500'
                  >
                    <a
                      href={data.cj_link}
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      {data.name}
                    </a>
                  </Text>

                  {productType === PRODUCT_CARD_TYPE.REVOLVE && (
                    <Text
                      className='ProductCard-title-ella'
                      variant='text-s'
                    >
                      {USDFormatter.format(price as number)}
                    </Text>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              <Text
                className='title-brand'
                variant='title-xxs-500'
              >
                {data.brand}
              </Text>
              <Text
                className='title-name'
                variant='text-s'
              >
                {data.name}
              </Text>
            </>
          )}
        </div>

        {productType !== PRODUCT_CARD_TYPE.REVOLVE && (
          <Text
            className='title-tier'
            variant='text-s'
          >
            {displayTierName(data.tier)}
          </Text>
        )}
      </div>
    </>
  );
}

function ProductCard(props: ProductCardProps) {
  const {
    className = '',
    small = false,
    data,
    newTab,
    variant = '',
    wishlistAnalytics,
    onProductCardClick
  } = props;
  const { isIOSWebView } = useContextBroker();
  const productUrl = generateEllaProductUrl(data, isIOSWebView);

  return (
    <div
      className={cn('ProductCard', className, variant, { small })}
      data-testid='product-card'
    >
      {isIOSWebView ? (
        // iOS web view and NextLink don't play well together. Using a standard HTML <a>
        <a
          href={productUrl}
          data-testid='product-card-link'
          onClick={onProductCardClick}
        >
          <ProductCardContent data={data} />
        </a>
      ) : (
        <Link
          href={productUrl}
          newTab={newTab}
          data-testid='product-card-link'
          // @ts-expect-error — Link's onClick type is mistyped (MouseEventHandler vs MouseEvent)
          onClick={onProductCardClick}
        >
          <ProductCardContent
            data={data}
            variant={variant}
          />
        </Link>
      )}

      {data.showWishlist === false ? null : (
        <WishlistIcon
          wishlistAnalytics={wishlistAnalytics}
          data={data}
        />
      )}
    </div>
  );
}

export default ProductCard;
